/**
 * © 2025 Little Shilling, Inc.
 * Shon Little
 * Created: 2025-09-19
 */

// Add third-party dependencies.
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Card,
  CardContent,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Alert,
  Divider,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  ArrowForward as ArrowForwardIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

// Add local dependencies.
import { SubscriptionPlan } from '../../types/subscription';
import { formatCurrency } from '../../utils/currency';

/**
 * Props for the PlanChangeConfirmationDialog component.
 */
interface PlanChangeConfirmationDialogProps {
  /**
   * Whether the dialog is open.
   */
  open: boolean;
  /**
   * The current subscription plan.
   */
  currentPlan: SubscriptionPlan | null;
  /**
   * The new plan to change to.
   */
  newPlan: SubscriptionPlan | null;
  /**
   * Callback when the user confirms the plan change.
   */
  onConfirm: () => void;
  /**
   * Callback when the user cancels the plan change.
   */
  onCancel: () => void;
  /**
   * Whether the plan change is in progress.
   */
  isLoading?: boolean;
}

/**
 * Confirmation dialog for subscription plan changes.
 * Shows a side-by-side comparison of current vs new plan.
 *
 * @param props - The component props
 * @returns {React.ReactElement} The plan change confirmation dialog
 */
const PlanChangeConfirmationDialog: React.FC<
  PlanChangeConfirmationDialogProps
> = ({
  open,
  currentPlan,
  newPlan,
  onConfirm,
  onCancel,
  isLoading = false,
}) => {
  const { t } = useTranslation('billing');
  if (!currentPlan || !newPlan) {
    return null;
  }

  const isUpgrade =
    (currentPlan.price_monthly || 0) < (newPlan.price_monthly || 0);
  const isDowngrade =
    (currentPlan.price_monthly || 0) > (newPlan.price_monthly || 0);
  let actionType: string;
  if (isUpgrade) {
    actionType = t('plans.upgrade');
  } else if (isDowngrade) {
    actionType = t('plans.downgrade');
  } else {
    actionType = t('plans.change');
  }

  return (
    <Dialog
      open={open}
      onClose={onCancel}
      maxWidth="md"
      fullWidth
      aria-labelledby="plan-confirmation-dialog-title"
    >
      <DialogTitle id="plan-confirmation-dialog-title">
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Typography variant="h6">{t('plans.confirmChange')}</Typography>
          <Button
            onClick={onCancel}
            sx={{ minWidth: 'auto' }}
            disabled={isLoading}
          >
            <CloseIcon />
          </Button>
        </Box>
      </DialogTitle>

      <DialogContent>
        {/* Beta Notice */}
        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="body2">
            <strong>{t('plans.betaPeriod')}</strong> {t('plans.betaNotice')}
          </Typography>
        </Alert>

        {/* Plan Comparison */}
        <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
          {t('plans.planComparison')}
        </Typography>

        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            gap: 2,
            alignItems: 'stretch',
          }}
        >
          {/* Current Plan */}
          <Box sx={{ flex: { xs: '1', md: '5' } }}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="overline" color="text.secondary">
                  {t('plans.current')}
                </Typography>
                <Box
                  sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}
                >
                  <Typography variant="h5">{currentPlan.name}</Typography>
                  <Chip
                    label={t('plans.current')}
                    color="success"
                    size="small"
                  />
                </Box>
                <Typography variant="h6" color="primary" gutterBottom>
                  {currentPlan.is_beta_free
                    ? '$0.00'
                    : formatCurrency(currentPlan.price_monthly)}
                  /{t('plans.perMonth')}
                </Typography>
                {currentPlan.is_beta_free && (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    gutterBottom
                  >
                    {t('plans.betaFree')}
                  </Typography>
                )}

                <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
                  {t('plans.features')}
                </Typography>
                <List dense>
                  <ListItem disablePadding>
                    <ListItemIcon sx={{ minWidth: 28 }}>
                      <CheckCircleIcon color="success" fontSize="small" />
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        currentPlan.max_owned_studios === 0
                          ? t('features.unlimitedStudios')
                          : t('features.upToStudios', {
                              count: currentPlan.max_owned_studios,
                              plural:
                                currentPlan.max_owned_studios !== 1 ? 's' : '',
                            })
                      }
                    />
                  </ListItem>
                  <ListItem disablePadding>
                    <ListItemIcon sx={{ minWidth: 28 }}>
                      <CheckCircleIcon color="success" fontSize="small" />
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        currentPlan.max_users_per_studio === 0
                          ? t('features.unlimitedUsers')
                          : t('features.upToUsers', {
                              count: currentPlan.max_users_per_studio,
                            })
                      }
                    />
                  </ListItem>
                  {(currentPlan.features || [])
                    .slice(0, 3)
                    .map((feature: string) => (
                      <ListItem key={feature} disablePadding>
                        <ListItemIcon sx={{ minWidth: 28 }}>
                          <CheckCircleIcon color="success" fontSize="small" />
                        </ListItemIcon>
                        <ListItemText primary={feature} />
                      </ListItem>
                    ))}
                  {(currentPlan.features || []).length > 3 && (
                    <ListItem disablePadding>
                      <ListItemText
                        primary={t('plans.moreFeatures', {
                          count: (currentPlan.features || []).length - 3,
                        })}
                        sx={{ fontStyle: 'italic', color: 'text.secondary' }}
                      />
                    </ListItem>
                  )}
                </List>
              </CardContent>
            </Card>
          </Box>

          {/* Arrow */}
          <Box
            sx={{
              flex: { xs: '1', md: '2' },
              textAlign: 'center',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              py: { xs: 2, md: 0 },
            }}
          >
            <ArrowForwardIcon color="primary" sx={{ fontSize: 40 }} />
            <Typography variant="body2" color="text.secondary">
              {t('plans.actionTo', { action: actionType })}
            </Typography>
          </Box>

          {/* New Plan */}
          <Box sx={{ flex: { xs: '1', md: '5' } }}>
            <Card
              variant="outlined"
              sx={{
                border: 2,
                borderColor: 'primary.main',
                backgroundColor: 'primary.50',
              }}
            >
              <CardContent>
                <Typography variant="overline" color="primary">
                  {t('plans.new')}
                </Typography>
                <Box
                  sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}
                >
                  <Typography variant="h5">{newPlan.name}</Typography>
                  {newPlan.name === 'Growth' && (
                    <Chip
                      label={t('plans.mostPopular')}
                      color="primary"
                      size="small"
                    />
                  )}
                </Box>
                <Typography variant="h6" color="primary" gutterBottom>
                  {newPlan.is_beta_free
                    ? '$0.00'
                    : formatCurrency(newPlan.price_monthly)}
                  /{t('plans.perMonth')}
                </Typography>
                {newPlan.is_beta_free && (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    gutterBottom
                  >
                    {t('plans.betaFree')}
                  </Typography>
                )}

                <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
                  {t('plans.features')}
                </Typography>
                <List dense>
                  <ListItem disablePadding>
                    <ListItemIcon sx={{ minWidth: 28 }}>
                      <CheckCircleIcon color="success" fontSize="small" />
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        newPlan.max_owned_studios === 0
                          ? t('features.unlimitedStudios')
                          : t('features.upToStudios', {
                              count: newPlan.max_owned_studios,
                              plural:
                                newPlan.max_owned_studios !== 1 ? 's' : '',
                            })
                      }
                    />
                  </ListItem>
                  <ListItem disablePadding>
                    <ListItemIcon sx={{ minWidth: 28 }}>
                      <CheckCircleIcon color="success" fontSize="small" />
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        newPlan.max_users_per_studio === 0
                          ? t('features.unlimitedUsers')
                          : t('features.upToUsers', {
                              count: newPlan.max_users_per_studio,
                            })
                      }
                    />
                  </ListItem>
                  {(newPlan.features || [])
                    .slice(0, 3)
                    .map((feature: string) => (
                      <ListItem key={feature} disablePadding>
                        <ListItemIcon sx={{ minWidth: 28 }}>
                          <CheckCircleIcon color="success" fontSize="small" />
                        </ListItemIcon>
                        <ListItemText primary={feature} />
                      </ListItem>
                    ))}
                  {(newPlan.features || []).length > 3 && (
                    <ListItem disablePadding>
                      <ListItemText
                        primary={t('plans.moreFeatures', {
                          count: (newPlan.features || []).length - 3,
                        })}
                        sx={{ fontStyle: 'italic', color: 'text.secondary' }}
                      />
                    </ListItem>
                  )}
                </List>
              </CardContent>
            </Card>
          </Box>
        </Box>

        {/* Summary */}
        <Divider sx={{ my: 3 }} />
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="body1" gutterBottom>
            <strong>{t('plans.summary')}</strong>{' '}
            {t('plans.aboutToChange', { action: actionType.toLowerCase() })}{' '}
            <strong>{currentPlan.name}</strong> {t('plans.to')}{' '}
            <strong>{newPlan.name}</strong>
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('plans.effectImmediate')}
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button onClick={onCancel} disabled={isLoading} variant="outlined">
          {t('plans.cancel')}
        </Button>
        <Button
          onClick={onConfirm}
          disabled={isLoading}
          variant="contained"
          color="primary"
          autoFocus
        >
          {isLoading
            ? t('plans.changingPlan')
            : t('plans.confirmAction', { action: actionType })}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PlanChangeConfirmationDialog;
